import { ArrowRight, Star, Crown, Award, Zap, BookOpen, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePromotional } from '../../contexts/PromotionalContext';
import CouponBanner from '../ui/CouponBanner';

const CourseBundlesSection = () => {
  const { config, isActive } = usePromotional();
  
  const bundles = [
    {
      id: 'qa-beginner',
      title: 'QA Beginner Bundle',
      subtitle: 'Perfect Starting Point for Manual Testers',
      description: 'Complete foundation package for QA professionals new to AI testing. Start your AI journey with confidence.',
      badge: 'MOST RECOMMENDED',
      badgeColor: 'bg-green-500',
      bestSeller: true,
      level: 'Beginner',
      duration: '28 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-green-500 to-emerald-600',
      borderColor: 'border-green-300 hover:border-green-500',
      bgColor: 'bg-green-50',
      courses: [
        'Using Gen AI & AI Agent in Software Automation Testing',
        'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
        'GenAI Multi-Agent Systems Testing'
      ],
      skills: [
        'AI Testing Fundamentals',
        'DeepEval Framework',
        'RAGAs Testing',
        'Multi-Agent Systems',
        'Prompt Engineering',
        'AI Test Automation'
      ],
      perfectFor: [
        'Manual QA Testers',
        'QA Engineers (0-3 years)',
        'Testing newcomers to AI',
        'Career switchers'
      ],
      udemy: [
        'generative-ai-in-software-automation-testing',
        'ai-testing-deepeval-ragas-ollama',
        'genai-multi-agent-software-testing'
      ]
    },
    {
      id: 'qa-professional',
      title: 'QA Professional Bundle',
      subtitle: 'Advanced AI Testing for Experienced QAs',
      description: 'Comprehensive AI testing mastery for experienced QA professionals ready to lead AI testing initiatives.',
      badge: 'POPULAR CHOICE',
      badgeColor: 'bg-blue-500',
      bestSeller: false,
      level: 'Intermediate',
      duration: '67 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-blue-500 to-cyan-600',
      borderColor: 'border-blue-300 hover:border-blue-500',
      bgColor: 'bg-blue-50',
      courses: [
        'Using Gen AI & AI Agent in Software Automation Testing',
        'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
        'E2E Testing ChatBot, AI Agent, RAG, MCP Server with DeepEval',
        'GenAI Multi-Agent Systems Testing',
        'AI-Driven Test Automation: Playwright, Selenium, LLMs & More'
      ],
      skills: [
        'Advanced AI Testing',
        'Complex Multi-Agent Systems',
        'Enterprise AI Validation',
        'AI Testing Leadership',
        'Advanced Frameworks',
        'Testing Strategy'
      ],
      perfectFor: [
        'Senior QA Engineers',
        'QA Team Leads',
        'Testing Consultants',
        'QA Managers'
      ],
      udemy: [
        'generative-ai-in-software-automation-testing',
        'ai-testing-deepeval-ragas-ollama',
        'ai-testing-deepeval-chatbot-rag-agent-mcp',
        'genai-multi-agent-software-testing',
        'ai-test-automation-selenium-playwright-llms'
      ]
    },
    {
      id: 'sdet-focused',
      title: 'SDET Complete Bundle',
      subtitle: 'Full-Stack AI Testing & Development',
      description: 'Complete AI testing and development package for SDETs who want to build and test AI systems end-to-end.',
      badge: 'COMPREHENSIVE',
      badgeColor: 'bg-purple-500',
      bestSeller: false,
      level: 'Advanced',
      duration: '82 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-purple-500 to-pink-600',
      borderColor: 'border-purple-300 hover:border-purple-500',
      bgColor: 'bg-purple-50',
      courses: [
        'Using Gen AI & AI Agent in Software Automation Testing',
        'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
        'E2E Testing ChatBot, AI Agent, RAG, MCP Server with DeepEval',
        'Build Agentic AI & Multi-Agent System for Software Testing',
        'GenAI Multi-Agent Systems Testing',
        'AI-Driven Test Automation: Playwright, Selenium, LLMs & More'
      ],
      skills: [
        'AI Agent Development',
        'ChatBot Creation',
        'RAG Systems',
        'Advanced Testing Frameworks',
        'AI System Architecture',
        'End-to-End AI Testing'
      ],
      perfectFor: [
        'SDET Engineers',
        'Test Automation Engineers',
        'Full-Stack QA Engineers',
        'Technical QA Leads'
      ],
      udemy: [
        'generative-ai-in-software-automation-testing',
        'ai-testing-deepeval-ragas-ollama', 
        'ai-testing-deepeval-chatbot-rag-agent-mcp',
        'build-ai-agent-chatbot-rag-langchain-local-llm',
        'genai-multi-agent-software-testing',
        'ai-test-automation-selenium-playwright-llms'
      ]
    },
    {
      id: 'sdet-dev-master',
      title: 'SDET + Dev Master Bundle',
      subtitle: 'Complete AI Engineering Mastery',
      description: 'Ultimate package for technical professionals who want to master both AI development and testing, including model fine-tuning.',
      badge: 'EXPERT LEVEL',
      badgeColor: 'bg-orange-500',
      bestSeller: false,
      level: 'Expert',
      duration: '87 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-orange-500 to-red-600',
      borderColor: 'border-orange-300 hover:border-orange-500',
      bgColor: 'bg-orange-50',
      courses: [
        'Using Gen AI & AI Agent in Software Automation Testing',
        'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
        'E2E Testing ChatBot, AI Agent, RAG, MCP Server with DeepEval',
        'Build Agentic AI & Multi-Agent System for Software Testing', 
        'GenAI Multi-Agent Systems Testing',
        'LLM Fine-Tuning for Beginners: HuggingFace & Unsloth'
      ],
      skills: [
        'Model Fine-tuning',
        'Unsloth & HuggingFace Mastery',
        'Custom AI Development',
        'Advanced AI Architecture',
        'Production AI Systems',
        'AI DevOps & Testing'
      ],
      perfectFor: [
        'Senior SDET Engineers',
        'AI/ML Engineers',
        'Technical Architects',
        'AI Development Leads'
      ],
      udemy: [
        'generative-ai-in-software-automation-testing',
        'ai-testing-deepeval-ragas-ollama',
        'ai-testing-deepeval-chatbot-rag-agent-mcp',
        'build-ai-agent-chatbot-rag-langchain-local-llm', 
        'genai-multi-agent-software-testing',
        'ai-with-huggingface'
      ]
    }
  ];

  return (
    <section className="py-20 px-4 bg-white" id="main">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Choose Your <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Career Bundle</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Save money and learn faster with our carefully curated course bundles designed for different career paths and experience levels.
          </p>
        </div>

        {/* Bundle Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-16">
          {bundles.map((bundle, index) => (
            <div 
              key={bundle.id} 
              className={`relative ${bundle.bgColor} rounded-3xl p-6 md:p-8 border-2 ${bundle.borderColor} transition-all duration-300 hover:shadow-2xl hover:scale-105 animate-fade-in group ${
                bundle.bestSeller ? 'ring-4 ring-green-200 shadow-2xl scale-105' : ''
              }`}
              style={{animationDelay: `${index * 200}ms`}}
            >
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className={`${bundle.badgeColor} text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center space-x-2`}>
                  {bundle.bestSeller && <Crown className="w-4 h-4" aria-label="Crown icon" />}
                  <span>{bundle.badge}</span>
                  {bundle.bestSeller && <Star className="w-4 h-4 fill-current" aria-label="Star icon" />}
                </div>
              </div>

              <div className="mt-6">
                {/* Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{bundle.title}</h3>
                  <p className="text-lg font-semibold text-gray-700 mb-4">{bundle.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed">{bundle.description}</p>
                </div>

                {/* Pricing */}
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <span className="text-3xl font-bold text-gray-900">${bundle.bundlePrice}</span>
                    <span className="text-lg text-gray-500 line-through">${bundle.originalPrice}</span>
                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium">
                      Save ${bundle.savings}
                    </span>
                  </div>
                  <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                    <span className="flex items-center">
                      <BookOpen className="w-4 h-4 mr-1" aria-label="BookOpen icon" />
                      {bundle.courses.length} courses
                    </span>
                    <span className="flex items-center">
                      <Award className="w-4 h-4 mr-1" aria-label="Award icon" />
                      {bundle.level}
                    </span>
                    <span>{bundle.duration}</span>
                  </div>
                </div>

                {/* Coupon Code Display */}
                {config.isEnabled && isActive && (
                  <div className="mb-6">
                    <CouponBanner
                      code={config.couponCode}
                      description={config.description}
                      variant="card"
                      showCopy={false}
                      className="mx-4"
                    />
                  </div>
                )}

                {/* Course List */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Courses Included:</h4>
                  <div className="space-y-2">
                    {bundle.courses.map((course, courseIndex) => (
                      <div key={courseIndex} className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" aria-label="CheckCircle icon" />
                        <span className="text-gray-700 text-sm">{course}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Skills Grid */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Skills You'll Master:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {bundle.skills.slice(0, 6).map((skill, skillIndex) => (
                      <span 
                        key={skillIndex}
                        className="px-3 py-2 bg-white/80 border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 text-center"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Perfect For */}
                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-3">Perfect For:</h4>
                  <div className="flex flex-wrap gap-2">
                    {bundle.perfectFor.map((role, roleIndex) => (
                      <span 
                        key={roleIndex}
                        className="px-3 py-1 bg-white border border-gray-300 rounded-full text-xs sm:text-sm text-gray-700"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <Link
                  to={`/bundle/${bundle.id}`}
                  className={`w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r ${bundle.color} text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group/btn text-lg`}
                >
                  <Crown className="w-5 h-5 mr-2" aria-label="Crown icon" />
                  Get {bundle.title}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" aria-label="ArrowRight icon" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bundle Benefits */}
        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Why Choose Course Bundles?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" aria-label="Award icon" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Significant Savings</h4>
              <p className="text-gray-600 text-sm">Save up to $100 compared to buying courses individually. Get more value for your investment.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-white" aria-label="BookOpen icon" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Structured Learning</h4>
              <p className="text-gray-600 text-sm">Courses are carefully sequenced to build upon each other for optimal learning progression.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-white" aria-label="Zap icon" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Career Focused</h4>
              <p className="text-gray-600 text-sm">Each bundle is tailored to specific career paths and professional development goals.</p>
            </div>
          </div>
        </div>

        {/* Individual Courses Note */}
        <div className="mt-12 text-center bg-blue-50 rounded-2xl p-6 border border-blue-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Prefer Individual Courses?</h4>
          <p className="text-gray-600 mb-4">
            You can also purchase courses individually if you prefer a specific topic or want to start with just one course.
          </p>
          <Link
            to="/courses"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors"
          >
            View Individual Course Details
            <ArrowRight className="w-4 h-4 ml-2" aria-label="ArrowRight icon" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CourseBundlesSection;